import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@resource-kit/ui/react';
import { resourceKeys } from '@resource-kit/controllers';
import {
  WEBHOOK_EVENT_STATUS,
  WEBHOOK_RESOURCE_SLUG,
  webhookEventsAPI,
  type WebhookEventReplayResult,
} from './webhookResource';

/**
 * Operator recovery for ONE stuck webhook event (#2220, ADR-0036 decision B).
 *
 * A D1 overload now skips the failure-inbox write (decision A), so a stranded
 * delivery leaves a `received` row and NOTHING in the failure panel — the
 * existing replay entry point is keyed by failure id and cannot reach it. This
 * button is that reach: it POSTs the event's own id and the backend re-drives
 * the stored bytes through the live ingestion core.
 *
 * Gated on `status === 'received'`: a `completed` row is done and a `failed`
 * row belongs to the failure panel, which owns replay for rows that have an
 * inbox entry. The check is a UX affordance only — the backend is the
 * authority and answers 404/409 on its own terms.
 *
 * WHO may press it is a separate gate and is NOT decided here: the tab itself
 * is declared `gated` and the host resolves the `webhook_failures:write`
 * verdict, so a read-only operator never reaches this component.
 *
 * Deliberately no list panel here: the `/overdue` diagnostic that finds these
 * rows in bulk is #2284. This surface only acts on the record already open.
 */
export function WebhookRecoveryCard({ eventId, status }: { eventId: string; status: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // The same closed-set member the backend's own guard compares against
  // (`replayWebhookEvent` refuses anything else with a 409), read from the
  // resource's status map rather than retyped here.
  const replayable = status === WEBHOOK_EVENT_STATUS.RECEIVED;

  const replay = useMutation<WebhookEventReplayResult, Error, void>({
    mutationFn: () => webhookEventsAPI.replay(eventId),
    onSuccess: () => {
      // The replay terminalises the row, so the record on screen and every
      // cached page of the list are both stale.
      queryClient.invalidateQueries({ queryKey: resourceKeys(WEBHOOK_RESOURCE_SLUG).all() });
    },
  });

  return (
    <section className="lg:col-span-2 space-y-2">
      <div className="text-sm font-semibold text-foreground">{t('webhook.recovery.title')}</div>
      <p className="text-sm text-muted-foreground">{t('webhook.recovery.description')}</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!replayable || replay.isPending}
        onClick={() => replay.mutate()}
      >
        {replay.isPending ? t('webhook.recovery.replaying') : t('webhook.recovery.replay')}
      </Button>
      {!replayable ? (
        <p className="text-sm text-muted-foreground">{t('webhook.recovery.notStuck')}</p>
      ) : null}
      {replay.isSuccess ? (
        <p className="text-sm text-foreground">
          {t('webhook.recovery.replayed', { outcome: replay.data.outcome })}
        </p>
      ) : null}
      {replay.isError ? (
        <p className="text-sm text-destructive">
          {t('webhook.recovery.replayError', { detail: replay.error.message })}
        </p>
      ) : null}
    </section>
  );
}

export default WebhookRecoveryCard;
